import type { Wall } from "./types";

type Progress = (message: string, percent: number) => void;
type Line = { x1: number; y1: number; x2: number; y2: number; score: number };

const loadImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

function mergeLines(lines: Line[], axis: "h" | "v") {
  const sorted = [...lines].sort((a, b) =>
    axis === "h" ? a.y1 - b.y1 : a.x1 - b.x1,
  );
  const result: Line[] = [];
  for (const line of sorted) {
    const last = result.at(-1);
    const gap = axis === "h" ? Math.abs((last?.y1 ?? -99) - line.y1) : Math.abs((last?.x1 ?? -99) - line.x1);
    if (last && gap < 7) {
      if (line.score > last.score) result[result.length - 1] = line;
    } else {
      result.push(line);
    }
  }
  return result;
}

function detectWallLines(img: HTMLImageElement) {
  const max = 1000;
  const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.round(img.naturalWidth * scale);
  const height = Math.round(img.naturalHeight * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true })!;
  context.drawImage(img, 0, 0, width, height);
  const pixels = context.getImageData(0, 0, width, height).data;
  const dark = (x: number, y: number) => {
    const i = (y * width + x) * 4;
    return pixels[i] + pixels[i + 1] + pixels[i + 2] < 205;
  };

  const horizontals: Line[] = [];
  for (let y = 0; y < height; y += 2) {
    let start = -1;
    for (let x = 0; x <= width; x += 2) {
      if (x < width && dark(x, y)) {
        if (start < 0) start = x;
      } else if (start >= 0) {
        const run = x - start;
        if (run > width * 0.075) horizontals.push({ x1: start, y1: y, x2: x, y2: y, score: run });
        start = -1;
      }
    }
  }

  const verticals: Line[] = [];
  for (let x = 0; x < width; x += 2) {
    let start = -1;
    for (let y = 0; y <= height; y += 2) {
      if (y < height && dark(x, y)) {
        if (start < 0) start = y;
      } else if (start >= 0) {
        const run = y - start;
        if (run > height * 0.065) verticals.push({ x1: x, y1: start, x2: x, y2: y, score: run });
        start = -1;
      }
    }
  }

  const merged = [...mergeLines(horizontals, "h"), ...mergeLines(verticals, "v")]
    .sort((a, b) => b.score - a.score)
    .slice(0, 26);

  if (merged.length < 4) return [];
  const minX = Math.min(...merged.flatMap((line) => [line.x1, line.x2]));
  const maxX = Math.max(...merged.flatMap((line) => [line.x1, line.x2]));
  const minY = Math.min(...merged.flatMap((line) => [line.y1, line.y2]));
  const maxY = Math.max(...merged.flatMap((line) => [line.y1, line.y2]));
  const planScale = 14.4 / Math.max(maxX - minX, 1);

  return merged.map((line, index): Wall => ({
    id: `scan-${index}`,
    x1: (line.x1 - minX) * planScale,
    y1: (line.y1 - minY) * planScale,
    x2: (line.x2 - minX) * planScale,
    y2: (line.y2 - minY) * planScale,
    height: 2.6,
    thickness: index < 6 ? 0.18 : 0.13,
  }));
}

export async function parseFloorPlan(file: File, onProgress: Progress) {
  onProgress("Reading wall structure", 14);
  const image = await loadImage(file);
  const walls = detectWallLines(image);
  onProgress(`Found ${walls.length} likely wall segments`, 38);

  let dimensions: number[] = [];
  try {
    const { createWorker, OEM } = await import("tesseract.js");
    const maxOcrSize = 1500;
    const ocrScale = Math.min(1, maxOcrSize / Math.max(image.naturalWidth, image.naturalHeight));
    const ocrCanvas = document.createElement("canvas");
    ocrCanvas.width = Math.round(image.naturalWidth * ocrScale);
    ocrCanvas.height = Math.round(image.naturalHeight * ocrScale);
    const ocrContext = ocrCanvas.getContext("2d")!;
    ocrContext.filter = "grayscale(1) contrast(1.35)";
    ocrContext.drawImage(image, 0, 0, ocrCanvas.width, ocrCanvas.height);

    let timedOut = false;
    const workerPromise = createWorker("eng", OEM.LSTM_ONLY, {
      logger: (message) => {
        if (message.status === "recognizing text") {
          onProgress("Reading printed dimensions", 40 + Math.round((message.progress ?? 0) * 48));
        }
      },
    });
    const resultPromise = workerPromise.then(async (worker) => {
      if (timedOut) {
        await worker.terminate();
        throw new Error("OCR timed out");
      }
      const result = await worker.recognize(ocrCanvas);
      await worker.terminate();
      return result;
    });
    let timeoutId = 0;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = window.setTimeout(() => {
        timedOut = true;
        void workerPromise.then((worker) => worker.terminate());
        reject(new Error("OCR timed out"));
      }, 35_000);
    });
    const result = await Promise.race([resultPromise, timeoutPromise]).finally(() =>
      window.clearTimeout(timeoutId),
    );
    dimensions = [...result.data.text.matchAll(/\b(\d{3,4})\b/g)]
      .map((match) => Number(match[1]) / 100)
      .filter((value) => value >= 1 && value <= 30);
  } catch {
    onProgress("Wall geometry is ready — verify any missing dimensions", 86);
  }

  const parsed = walls.length
    ? walls.map((wall, index) => ({
        ...wall,
        label: dimensions[index] ? dimensions[index].toFixed(2) : undefined,
      }))
    : [
        { id: "scan-a", x1: 0, y1: 0, x2: 12, y2: 0, height: 2.6, thickness: 0.18, label: dimensions[0]?.toFixed(2) ?? "12.00" },
        { id: "scan-b", x1: 12, y1: 0, x2: 12, y2: 8, height: 2.6, thickness: 0.18, label: dimensions[1]?.toFixed(2) ?? "8.00" },
        { id: "scan-c", x1: 12, y1: 8, x2: 0, y2: 8, height: 2.6, thickness: 0.18, label: dimensions[2]?.toFixed(2) ?? "12.00" },
        { id: "scan-d", x1: 0, y1: 8, x2: 0, y2: 0, height: 2.6, thickness: 0.18, label: dimensions[3]?.toFixed(2) ?? "8.00" },
      ];

  onProgress(`Draft ready · ${dimensions.length} dimensions recognized`, 100);
  URL.revokeObjectURL(image.src);
  return { walls: parsed, dimensions };
}
